import { isValidElement, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import HoverTooltip from '@/components/tooltip/HoverTooltip.jsx';

import './StatusPill.css';
import { toneFor, TONE_ICONS } from './statusTones.js';

const VALID_TONES = new Set(['success', 'warning', 'danger', 'info', 'neutral']);

/**
 * Shared status/lifecycle pill. Reproduces the .game-features-table-state look (radius-full,
 * color-mix tint bg + token color) as one reusable primitive so grids stop hand-rolling their own
 * status badges.
 *
 * Usage:
 *   <StatusPill status={row.state} />                 // resolves label + tone via statusTones
 *   <StatusPill tone="success">Active</StatusPill>    // explicit tone + label
 *   <StatusPill status={row.state} tone="info" />     // resolve label, override tone
 *
 * Icons are OPT-IN — a pill with no `icon` renders exactly as it always has, which is why the
 * existing table call sites did not change appearance when this was added. Three ways to ask:
 *
 *   <StatusPill status="ACTIVE" icon="auto" />              // statusTones' glyph for that state
 *   <StatusPill status="FAILED" icon={faBug} />             // any FontAwesome icon
 *   <StatusPill tone="info" icon={<MySvg/>}>Synced</StatusPill>  // any node (custom SVG, dot, …)
 *
 * `icon="auto"` (or `icon` as a bare boolean, matching StatusBanner) takes the per-status glyph from
 * statusTones.js and falls back to the per-tone one, so an unmapped enum still gets a sensible mark
 * rather than nothing. `iconPosition="right"` trails it instead, as on Button.
 *
 * The icon is decorative: the label already states the status, so it is aria-hidden and never the
 * only carrier of meaning. Don't ship an icon-only pill — there is no accessible name to give it.
 *
 * ── Responsiveness ───────────────────────────────────────────────────────────────────────────────
 * Two strategies, the same two Button offers and named the same way, so a pill and a button in the
 * same cramped cell behave alike:
 *
 *   overflow="ellipsis"  (default) the label clips with an ellipsis and the full text is available
 *                        on hover. The tooltip is only armed when the label is ACTUALLY clipped, so
 *                        a pill with room to breathe never grows a spurious tooltip.
 *   overflow="wrap"      the pill grows taller instead of clipping: icon and label wrap as flex
 *                        items first, then the label breaks inside words (overflow-wrap: anywhere)
 *                        so one long unbroken token cannot push past the container.
 *
 * Either way the pill takes `max-width: 100%` and the label `min-width: 0`, which is what lets it
 * shrink inside a narrow grid cell at all — a flex item defaults to min-width:auto and would
 * otherwise refuse to go below its text width and overflow the cell instead.
 *
 * `justify` distributes the icon and label along the pill: center (default) | between | evenly. It
 * only has anything to distribute when the pill is WIDER than its content, i.e. with `isFullWidth`
 * or a width from the call site — on an auto-width pill the content already fills it exactly.
 *
 * Renders nothing when there's no status and no explicit label (matches the old per-page
 * `if (!state) return null` behaviour), so empty cells stay empty.
 *
 * `onClick` makes the pill a button, for a tag that opens what it names.
 */

/* A FontAwesome icon definition is a plain object with an `iconName` (or the [prefix, name] tuple
   form) — anything else that is not one of those is treated as a node and rendered as given. */
const isFaIcon = (v) => Array.isArray(v)
    || (typeof v === 'object' && v !== null && !isValidElement(v) && 'iconName' in v);

/* Accept either this codebase's short spelling (Button's `align`) or the raw CSS keyword, so
   justify="space-between" and justify="between" mean the same thing rather than one of them
   silently falling back to center. */
const JUSTIFY = {
    center: 'center',
    between: 'between',
    evenly: 'evenly',
    'space-between': 'between',
    'space-evenly': 'evenly',
};

const cx = (...parts) => parts.filter(Boolean).join(' ');

export default function StatusPill({
    status,
    tone,
    label,
    icon,
    iconPosition = 'left',
    size = 'medium',
    overflow = 'ellipsis',
    justify = 'center',
    isFullWidth = false,
    onClick,
    children,
    className = '',
}) {
    const labelRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const resolved = toneFor(status);
    const finalLabel = label ?? children ?? resolved?.label ?? null;
    const isEmpty = finalLabel == null || finalLabel === '';

    /* Only a plain-string label can be re-shown verbatim in a tooltip, and only the ellipsis
       strategy clips anything worth re-showing — a wrapped pill already shows all its text. */
    const wantsTooltip = !isEmpty && overflow === 'ellipsis' && typeof finalLabel === 'string';

    useEffect(() => {
        if (!wantsTooltip) {
            setIsTruncated(false);
            return undefined;
        }
        const el = labelRef.current;
        if (!el) return undefined;

        // +1 absorbs sub-pixel rounding, which otherwise reports a snugly-fitting label as clipped.
        const check = () => setIsTruncated(el.scrollWidth > el.clientWidth + 1);
        check();
        const observer = new ResizeObserver(check);
        observer.observe(el);
        return () => observer.disconnect();
    }, [wantsTooltip, finalLabel]);

    // Placed after the hooks so hook order stays stable when a pill flips between empty and filled.
    if (isEmpty) return null;

    const picked = tone || resolved?.tone || 'neutral';
    const finalTone = VALID_TONES.has(picked) ? picked : 'neutral';
    const finalJustify = JUSTIFY[justify] || 'center';

    const pickedIcon = (icon === true || icon === 'auto')
        ? (resolved?.icon || TONE_ICONS[finalTone] || null)
        : (icon || null);

    let iconEl = null;
    if (pickedIcon) {
        iconEl = isFaIcon(pickedIcon)
            ? <FontAwesomeIcon icon={pickedIcon} className="status-pill-icon" />
            : <span className="status-pill-icon" aria-hidden="true">{pickedIcon}</span>;
    }

    const Tag = onClick ? 'button' : 'span';
    const pill = (
        <Tag
            type={onClick ? 'button' : undefined}
            onClick={onClick}
            className={cx(
                'status-pill',
                `status-pill--${finalTone}`,
                size === 'small' && 'status-pill--small',
                overflow === 'wrap' && 'status-pill--wrap',
                finalJustify !== 'center' && `status-pill--justify-${finalJustify}`,
                isFullWidth && 'status-pill--full',
                onClick && 'status-pill--clickable',
                className,
            )}
        >
            {iconPosition === 'left' && iconEl}
            <span ref={labelRef} className="status-pill-label">{finalLabel}</span>
            {iconPosition === 'right' && iconEl}
        </Tag>
    );

    if (!wantsTooltip) return pill;

    return (
        <HoverTooltip
            className={cx('status-pill-tip-anchor', isFullWidth && 'status-pill-tip-anchor--full')}
            panelClassName="status-pill-tip"
            content={finalLabel}
            disabled={!isTruncated}
        >
            {pill}
        </HoverTooltip>
    );
}

StatusPill.propTypes = {
    // Raw state/status enum (e.g. 'ACTIVE', 'DRAFT'); resolved to label+tone+icon via statusTones.
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Explicit tone override.
    tone: PropTypes.oneOf(['success', 'warning', 'danger', 'info', 'neutral']),
    // Explicit label override (else derived from `status`).
    label: PropTypes.node,
    /* Leading/trailing glyph. `true` or 'auto' resolves it from the status (then the tone); a
       FontAwesome icon definition or any node is used as given. Omit for no icon. */
    icon: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.oneOf(['auto']),
        PropTypes.object,
        PropTypes.array,
        PropTypes.node,
    ]),
    iconPosition: PropTypes.oneOf(['left', 'right']),
    /* Density. 'small' is the label-dense variant for rows that wear several pills at once — a
       config branch carries its state, its audience and its delivery gates side by side, and at
       the default size that row is mostly pill. Same tones, same shape, lighter type. */
    size: PropTypes.oneOf(['medium', 'small']),
    // Cramped-space strategy: clip with a hover tooltip, or grow taller and break words.
    overflow: PropTypes.oneOf(['ellipsis', 'wrap']),
    // Distribution of icon + label; needs a pill wider than its content to show any effect.
    justify: PropTypes.oneOf(['center', 'between', 'evenly', 'space-between', 'space-evenly']),
    // Stretch to the container width, giving `justify` room to work.
    isFullWidth: PropTypes.bool,
    // Makes the pill a button.
    onClick: PropTypes.func,
    children: PropTypes.node,
    className: PropTypes.string,
};
